/**
 * StoreIntegrationPage.tsx (AbstractLicensingBackend)
 *
 * Copyright © 2026 InstaLOD GmbH - All Rights Reserved.
 *
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * This file and all its contents are proprietary and confidential.
 *
 * Maintained by Rogiel Sulzbach, 2026
 *
 * @file StoreIntegrationPage.tsx
 * @author Rogiel Sulzbach
 * @copyright 2026 InstaLOD GmbH. All rights reserved.
 * @section License
 */

import React, { useEffect, useState } from 'react';
import Alert from 'react-bootstrap/Alert';
import Button from 'react-bootstrap/Button';
import Col from 'react-bootstrap/Col';
import Form from 'react-bootstrap/Form';
import Row from 'react-bootstrap/Row';
import { Column } from 'primereact/column';
import { Dropdown, DropdownChangeEvent } from 'primereact/dropdown';
import { Fieldset } from 'primereact/fieldset';
import { useTranslation } from 'react-i18next';
import BaseDatatable from '@abstract/abstractwebcommon-client/Table/BaseDatatable';
import DatatableColumn from '@abstract/abstractwebcommon-client/Table/DatatableColumn';
import InstaInputText from '@abstract/abstractwebcommon-client/FormControl/InstaInputText';
import FieldSkeleton, {
  SkeletonHeight,
  controlInputClassName
} from '@abstract/abstractwebcommon-client/SkeletonLoader/FieldSkeleton/FieldSkeleton';
import { showSuccessToast } from '@abstract/abstractwebcommon-client/AlertToast/AlertToast';
import { handleError } from '@abstract/abstractwebcommon-client/ErrorHandler/ErrorHandler';
import { asyncErrorHandler } from '@abstract/abstractwebcommon-shared/utils/AsyncErrorHandler';
import { formatDate } from '@abstract/abstractwebcommon-shared/utils/sharedFunctions';
import {
  IStoreConfiguration,
  IStoreStatus,
  IStoreVerifyResponse,
  IStoreWebhookLog,
  UNCHANGED_API_KEY,
  getStoreConfiguration,
  getStoreWebhookLogs,
  reconcileStoreNow,
  updateStoreConfiguration,
  verifyStoreCredentials
} from '../../../Services/StoreIntegration';
import './Style.css';

/** A replica that has not advanced in an hour is the incident; a single failed request is not. */
const STALE_AFTER_MS = 60 * 60 * 1000;

/**
 * `/admin/store` — the Abstract Store integration screen.
 *
 * Three `Fieldset` sections, the same shape the settings screen uses: status, configuration, and
 * **the inbound delivery log**. The log is the one that is easy to leave out and expensive to be
 * without — an integration that surfaces only errors cannot answer the question an operator actually
 * asks, "is the Store reaching us?", because a healthy quiet hour and a webhook URL that was never
 * registered look identical from the inside.
 *
 * The page heading comes from the route's `headingKey` via `AdminContent`, so this renders none of
 * its own.
 */
const StoreIntegrationPage = (): JSX.Element => {
  const { t } = useTranslation();

  const [configuration, setConfiguration] = useState<IStoreConfiguration | null>(null);
  const [status, setStatus] = useState<IStoreStatus | null>(null);
  const [logs, setLogs] = useState<IStoreWebhookLog[]>([]);
  const [logTotal, setLogTotal] = useState<number>(0);
  const [outcomeFilter, setOutcomeFilter] = useState<string>('');
  const [verifyResult, setVerifyResult] = useState<IStoreVerifyResponse | null>(null);

  const [isLoading, setLoading] = useState<boolean>(true);
  const [isSaving, setSaving] = useState<boolean>(false);
  const [isBusy, setBusy] = useState<boolean>(false);

  const [baseURL, setBaseURL] = useState<string>('');
  const [apiKey, setAPIKey] = useState<string>('');
  const [isAPIKeyDirty, setAPIKeyDirty] = useState<boolean>(false);
  const [reconcileInterval, setReconcileInterval] = useState<string>('300');
  const [retentionDays, setRetentionDays] = useState<string>('30');

  /** Skeleton and input classes while the settings are still in flight, as on the settings screen. */
  const renderSkeleton = (): JSX.Element =>
    !configuration && <FieldSkeleton skeletonHeightType={SkeletonHeight.Default} />;
  const inputClassName = (): string => controlInputClassName(!configuration);

  /** The outcomes the framework reports, plus an "everything" option. */
  const outcomeOptions = [
    { label: t('admin.page.store.outcome.all'), value: '' },
    { label: t('admin.page.store.outcome.applied'), value: 'Applied' },
    { label: t('admin.page.store.outcome.duplicate'), value: 'Duplicate' },
    { label: t('admin.page.store.outcome.ignored'), value: 'Ignored' },
    { label: t('admin.page.store.outcome.rejected'), value: 'Rejected' },
    { label: t('admin.page.store.outcome.failed'), value: 'Failed' }
  ];

  /** Colour by severity, so a wall of green reads as healthy at a glance. */
  const outcomeBadgeClass = (outcome: string): string => {
    switch (outcome) {
      case 'Applied':
        return 'badge badge-success';
      case 'Duplicate':
        return 'badge badge-info';
      case 'Ignored':
        return 'badge badge-warning';
      default:
        return 'badge badge-danger';
    }
  };

  const formatAge = (milliseconds: number | null): string => {
    if (milliseconds === null || milliseconds === undefined) {
      return t('admin.page.store.never');
    }
    const seconds: number = Math.floor(milliseconds / 1000);
    if (seconds < 60) {
      return `${seconds}s`;
    }
    if (seconds < 3600) {
      return `${Math.floor(seconds / 60)}m`;
    }
    return `${Math.floor(seconds / 3600)}h`;
  };

  const loadConfiguration = async (): Promise<void> => {
    setLoading(true);
    try {
      const response = await asyncErrorHandler(getStoreConfiguration());
      const data = response?.data;
      if (data) {
        setConfiguration(data.configuration);
        setStatus(data.status);
        setBaseURL(data.configuration.storeBaseURL ?? '');
        setReconcileInterval(String(data.configuration.storeReconcileIntervalSeconds ?? 300));
        setRetentionDays(String(data.configuration.storeWebhookLogRetentionDays ?? 30));
        setAPIKey('');
        setAPIKeyDirty(false);
      }
    } catch (error) {
      handleError({ message: t('admin.page.store.loadFailed') });
    } finally {
      setLoading(false);
    }
  };

  const loadLogs = async (outcome: string = outcomeFilter): Promise<void> => {
    try {
      const response = await asyncErrorHandler(getStoreWebhookLogs(outcome));
      const data = response?.data;
      if (data) {
        setLogs(data.logs ?? []);
        setLogTotal(data.total ?? 0);
      }
    } catch (error) {
      handleError({ message: t('admin.page.store.logsFailed') });
    }
  };

  useEffect(() => {
    loadConfiguration();
    loadLogs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSave = async (): Promise<void> => {
    setSaving(true);
    try {
      const response = await asyncErrorHandler(
        updateStoreConfiguration({
          storeBaseURL: baseURL,
          // Only ever sent when the field was actually touched. See UNCHANGED_API_KEY.
          storeAPIKey: isAPIKeyDirty ? apiKey : UNCHANGED_API_KEY,
          storeReconcileIntervalSeconds: Number(reconcileInterval),
          storeWebhookLogRetentionDays: Number(retentionDays)
        })
      );
      const data = response?.data;
      if (data) {
        setConfiguration(data.configuration);
        setStatus(data.status);
      }
      setAPIKey('');
      setAPIKeyDirty(false);
      setVerifyResult(null);
      showSuccessToast(t('admin.page.store.saved'));
    } catch (error) {
      handleError({ message: t('admin.page.store.saveFailed') });
    } finally {
      setSaving(false);
    }
  };

  const handleVerify = async (): Promise<void> => {
    setBusy(true);
    try {
      const response = await asyncErrorHandler(verifyStoreCredentials());
      setVerifyResult(response?.data ?? null);
    } catch (error) {
      handleError({ message: t('admin.page.store.verifyFailed') });
    } finally {
      setBusy(false);
    }
  };

  const handleReconcile = async (): Promise<void> => {
    setBusy(true);
    try {
      const response = await asyncErrorHandler(reconcileStoreNow());
      showSuccessToast(
        t('admin.page.store.reconciled', {
          applied: String(response?.data?.reconciled?.itemsApplied ?? 0),
          repaired: String(response?.data?.projectionsRepaired ?? 0)
        })
      );
      await loadConfiguration();
      await loadLogs();
    } catch (error) {
      handleError({ message: t('admin.page.store.reconcileFailed') });
    } finally {
      setBusy(false);
    }
  };

  const isReplicaStale: boolean =
    status !== null &&
    status.isConfigured &&
    (status.replicaAgeMs === null || status.replicaAgeMs > STALE_AFTER_MS);

  const webhookTotal: number = Object.values(status?.webhookOutcomesLast24h ?? {}).reduce(
    (sum: number, count: number) => sum + count,
    0
  );

  /** The status banner. One line, and the first thing an operator should read. */
  const renderStatusBanner = (): JSX.Element => {
    if (isLoading) {
      return <Alert variant="info">{t('admin.page.store.loading')}</Alert>;
    }
    if (!status?.isConfigured) {
      return <Alert variant="warning">{t('admin.page.store.notConfigured')}</Alert>;
    }
    if (isReplicaStale) {
      /* The thing worth alerting on: a cursor that falls far enough behind expires, and the only
         recovery is a full snapshot walk. */
      return <Alert variant="danger">{t('admin.page.store.replicaStale')}</Alert>;
    }
    return (
      <Alert variant="success">
        {t('admin.page.store.connected', { age: formatAge(status.replicaAgeMs) })}
      </Alert>
    );
  };

  const logTableHeader: JSX.Element = (
    <Row className="d-flex align-items-center justify-content-end mx-0">
      <Dropdown
        name="storeOutcomeFilter"
        optionLabel="label"
        optionValue="value"
        value={outcomeFilter}
        options={outcomeOptions}
        onChange={(event: DropdownChangeEvent) => {
          setOutcomeFilter(event.value);
          loadLogs(event.value);
        }}
      />
      <Button className="ml-2" variant="secondary" onClick={() => loadLogs()}>
        <span className="d-flex align-items-center">
          <i className="pi pi-refresh" style={{ fontSize: '1em' }} />
          <span className="flex-grow-1 ml-1">{t('admin.page.store.logs.refresh')}</span>
        </span>
      </Button>
    </Row>
  );

  return (
    <Form>
      <Row>
        <Col xs={12} className="pb-0">
          <Fieldset legend={t('admin.page.store.category.status')}>
            <Row>
              <Col xs={12}>{renderStatusBanner()}</Col>
            </Row>

            <Row>
              <Col xs={12} md={4}>
                <label className="font-weight-bold">
                  {t('admin.page.store.entitlementsHeld')}
                </label>
                <div>
                  {Object.keys(status?.entitlementCounts ?? {}).length > 0 ? (
                    Object.entries(status?.entitlementCounts ?? {}).map(([key, count]) => (
                      <span
                        key={key}
                        className={`mr-1 ${
                          key === 'expired' ? 'badge badge-secondary' : 'badge badge-success'
                        }`}>
                        {key}: {count}
                      </span>
                    ))
                  ) : (
                    <span className="text-muted">&mdash;</span>
                  )}
                </div>
              </Col>
              <Col xs={12} md={4} className="mt-3 mt-md-0">
                <label className="font-weight-bold">{t('admin.page.store.webhooks24h')}</label>
                <div>
                  {webhookTotal > 0 ? (
                    Object.entries(status?.webhookOutcomesLast24h ?? {}).map(([key, count]) => (
                      <span key={key} className={`mr-1 ${outcomeBadgeClass(key)}`}>
                        {key}: {count}
                      </span>
                    ))
                  ) : (
                    <span className="text-muted">{t('admin.page.store.noneReceived')}</span>
                  )}
                </div>
              </Col>
              <Col xs={12} md={4} className="mt-3 mt-md-0">
                <label className="font-weight-bold">{t('admin.page.store.webhookURL')}</label>
                <div>
                  <code>{status?.webhookPath}</code>
                </div>
                <small className="text-muted">{t('admin.page.store.webhookURLHelp')}</small>
              </Col>
            </Row>

            {verifyResult ? (
              <Row className="mt-3">
                <Col xs={12}>
                  {verifyResult.ok ? (
                    <Alert variant="success" className="mb-0">
                      {verifyResult.entitlementKeys.length > 0
                        ? t('admin.page.store.verifyOK', {
                            keys: verifyResult.entitlementKeys.join(', ')
                          })
                        : /* An empty scope is a registration problem, not a customer problem, and is
                             otherwise indistinguishable from "nobody has bought anything". */
                          t('admin.page.store.verifyNoScope')}
                    </Alert>
                  ) : (
                    <Alert variant="danger" className="mb-0">
                      {verifyResult.error}
                    </Alert>
                  )}
                </Col>
              </Row>
            ) : null}

            <Row className="mt-3">
              <Col xs="12" className="py-0">
                <Button
                  className="settingsForm ml-3 float-right"
                  disabled={isBusy || !status?.isConfigured}
                  onClick={handleReconcile}>
                  <span className="d-flex align-items-center">
                    <i className="pi pi-sync" style={{ fontSize: '1em' }} />
                    <span className="flex-grow-1 ml-1">
                      {t('admin.page.store.reconcileNow')}
                    </span>
                  </span>
                </Button>
                <Button
                  className="settingsForm float-right"
                  disabled={isBusy || !status?.isConfigured}
                  onClick={handleVerify}>
                  <span className="d-flex align-items-center">
                    <i className="pi pi-verified" style={{ fontSize: '1em' }} />
                    <span className="flex-grow-1 ml-1">{t('admin.page.store.verify')}</span>
                  </span>
                </Button>
              </Col>
            </Row>
          </Fieldset>

          <Fieldset legend={t('admin.page.store.category.configuration')}>
            <Row>
              <Form.Group as={Col} sm="12" md="6">
                <InstaInputText
                  label={t('admin.page.store.form.baseURL')}
                  name="storeBaseURL"
                  id={'storeBaseURL'}
                  isLoading={isLoading}
                  className={inputClassName()}
                  value={baseURL}
                  onChange={(event: any) => setBaseURL(event.target.value)}
                  onBlur={noValidation.onBlur}
                  errors={noValidation.errors}
                  touched={noValidation.touched}
                />
                {renderSkeleton()}
                <small className="text-muted">{t('admin.page.store.form.baseURLHelp')}</small>
              </Form.Group>

              <Form.Group as={Col} sm="12" md="6">
                <InstaInputText
                  label={t('admin.page.store.form.apiKey')}
                  name="storeAPIKey"
                  id={'storeAPIKey'}
                  type="password"
                  isLoading={isLoading}
                  className={inputClassName()}
                  value={isAPIKeyDirty ? apiKey : ''}
                  placeholder={
                    configuration?.hasAPIKey
                      ? t('admin.page.store.form.apiKeySet')
                      : t('admin.page.store.form.apiKeyPlaceholder')
                  }
                  onChange={(event: any) => {
                    setAPIKeyDirty(true);
                    setAPIKey(event.target.value);
                  }}
                  onBlur={noValidation.onBlur}
                  errors={noValidation.errors}
                  touched={noValidation.touched}
                />
                {renderSkeleton()}
                <small className="text-muted">
                  {configuration?.hasAPIKey
                    ? t('admin.page.store.form.apiKeyHelpStored')
                    : t('admin.page.store.form.apiKeyHelp')}
                </small>
                {isAPIKeyDirty && apiKey === '' ? (
                  <Alert variant="warning" className="mt-2 mb-0 py-2">
                    {t('admin.page.store.form.apiKeyClearWarning')}
                  </Alert>
                ) : null}
              </Form.Group>

              <Form.Group as={Col} sm="12" md="6">
                <InstaInputText
                  label={t('admin.page.store.form.interval')}
                  name="storeReconcileIntervalSeconds"
                  id={'storeReconcileIntervalSeconds'}
                  type="number"
                  isLoading={isLoading}
                  className={inputClassName()}
                  value={reconcileInterval}
                  onChange={(event: any) => setReconcileInterval(event.target.value)}
                  onBlur={noValidation.onBlur}
                  errors={noValidation.errors}
                  touched={noValidation.touched}
                />
                {renderSkeleton()}
                <small className="text-muted">{t('admin.page.store.form.intervalHelp')}</small>
              </Form.Group>

              <Form.Group as={Col} sm="12" md="6">
                <InstaInputText
                  label={t('admin.page.store.form.retention')}
                  name="storeWebhookLogRetentionDays"
                  id={'storeWebhookLogRetentionDays'}
                  type="number"
                  isLoading={isLoading}
                  className={inputClassName()}
                  value={retentionDays}
                  onChange={(event: any) => setRetentionDays(event.target.value)}
                  onBlur={noValidation.onBlur}
                  errors={noValidation.errors}
                  touched={noValidation.touched}
                />
                {renderSkeleton()}
              </Form.Group>
            </Row>

            <Row>
              <Col xs="12" className="py-0">
                <Button
                  className="settingsForm float-right"
                  disabled={isSaving}
                  onClick={handleSave}>
                  <span className="d-flex align-items-center">
                    <i className="pi pi-save" style={{ fontSize: '1em' }} />
                    <span className="flex-grow-1 ml-1">
                      {t('admin.page.store.form.save')}
                    </span>
                  </span>
                </Button>
              </Col>
            </Row>
          </Fieldset>

          <Fieldset legend={t('admin.page.store.category.log')}>
            <small className="text-muted d-block mb-2">
              {t('admin.page.store.logs.description')}
            </small>
            <BaseDatatable
              value={logs}
              totalRecords={logTotal}
              isLoading={isLoading}
              dataKey="id"
              header={logTableHeader}
              emptyMessage={t('admin.page.store.logs.empty')}
              responsive
              bodyStyle={'text-center'}>
              <Column
                field="receivedAt"
                header={t('admin.page.store.logs.received')}
                body={(rows: IStoreWebhookLog) => (
                  <DatatableColumn
                    title={t('admin.page.store.logs.received')}
                    data={formatDate(rows.receivedAt)}
                  />
                )}
                className="col-width-15"
              />
              <Column
                field="outcome"
                header={t('admin.page.store.logs.outcome')}
                body={(rows: IStoreWebhookLog) => (
                  <DatatableColumn
                    title={t('admin.page.store.logs.outcome')}
                    data={<span className={outcomeBadgeClass(rows.outcome)}>{rows.outcome}</span>}
                  />
                )}
                className="col-width-10"
              />
              <Column
                field="eventType"
                header={t('admin.page.store.logs.type')}
                body={(rows: IStoreWebhookLog) => (
                  <DatatableColumn
                    title={t('admin.page.store.logs.type')}
                    data={rows.eventType}
                  />
                )}
                className="d-none d-sm-table-cell col-width-15"
                headerClassName="d-none d-sm-table-cell col-width-15"
              />
              <Column
                field="userUUID"
                header={t('admin.page.store.logs.user')}
                body={(rows: IStoreWebhookLog) => (
                  <DatatableColumn
                    title={t('admin.page.store.logs.user')}
                    data={rows.userUUID}
                  />
                )}
                className="d-none d-xl-table-cell col-width-15"
                headerClassName="d-none d-xl-table-cell col-width-15"
              />
              <Column
                field="entitlementKey"
                header={t('admin.page.store.logs.entitlement')}
                body={(rows: IStoreWebhookLog) => (
                  <DatatableColumn
                    title={t('admin.page.store.logs.entitlement')}
                    data={rows.entitlementKey}
                  />
                )}
                className="col-width-15"
              />
              <Column
                field="detail"
                header={t('admin.page.store.logs.detail')}
                body={(rows: IStoreWebhookLog) => (
                  <DatatableColumn
                    title={t('admin.page.store.logs.detail')}
                    data={rows.detail}
                  />
                )}
                className="d-none d-xl-table-cell col-width-25"
                headerClassName="d-none d-xl-table-cell col-width-25"
              />
            </BaseDatatable>
          </Fieldset>
        </Col>
      </Row>
    </Form>
  );
};

/**
 * The validation props `InstaInputText` requires.
 *
 * This screen is not Formik-driven: there is nothing to validate client-side, because the server
 * normalises the base URL, floors the interval and owns the meaning of a blank API key. Supplying
 * them explicitly says "nothing has been touched and found invalid" rather than leaving required
 * props off and relying on the component tolerating undefined.
 */
const noValidation = {
  onBlur: (): void => undefined,
  errors: undefined as string | undefined,
  touched: false
};

export default StoreIntegrationPage;
